import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, KeyRound, Copy, Download, Check } from "lucide-react";

// Reusable TOTP MFA manager for any realm.
// props: client (axios), base (e.g. "/consumer/mfa"), enabled (bool), mandatory (bool),
//        onChange (fn), theme ("dark"|"light"), setupOnly (bool)
export function MfaManager({ client, base, enabled, mandatory, onChange, theme = "dark", setupOnly = false, emailReset = false }) {
  const [step, setStep] = useState("idle"); // idle | scan | recovery | disable | reset
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState([]);
  const [ack, setAck] = useState(false);
  const [copied, setCopied] = useState(false);
  const [disPw, setDisPw] = useState("");
  const [disCode, setDisCode] = useState("");
  const [resetCode, setResetCode] = useState("");

  const dark = theme === "dark";
  const input = dark
    ? "w-full rounded-lg px-3.5 h-11 text-[14px] text-white bg-white/[0.05] border border-white/10 outline-none focus:border-cyan-400/60 placeholder:text-white/30 tracking-widest"
    : "w-full rounded-lg px-3.5 h-11 text-[14px] bg-white border border-neutral-300 outline-none focus:border-neutral-500 placeholder:text-neutral-400 tracking-widest";
  const muted = dark ? "text-white/55" : "text-neutral-500";
  const btnPrimary = "px-5 h-11 rounded-lg text-[13.5px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50";

  const start = async () => {
    setBusy(true);
    try { const { data } = await client.post(`${base}/setup/start`); setQr(data.qr); setSecret(data.secret); setCode(""); setStep("scan"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start setup"); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`${base}/setup/confirm`, { code: code.trim() });
      setRecovery(data.recovery_codes || []); setAck(false); setStep("recovery");
      toast.success("Two-factor authentication is on");
    } catch (e) { toast.error(e.response?.data?.detail || "That code isn't valid"); }
    finally { setBusy(false); }
  };
  const requestReset = async () => {
    setBusy(true);
    try { await client.post(`${base}/reset/request`); toast.success("We emailed you a reset code"); setResetCode(""); setStep("reset"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't send a reset code"); }
    finally { setBusy(false); }
  };
  const confirmReset = async () => {
    setBusy(true);
    try {
      await client.post(`${base}/reset/confirm`, { code: resetCode.trim() });
      toast.success("Two-factor authentication turned off"); setStep("idle"); setResetCode(""); onChange && onChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "That code isn't valid"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await client.post(`${base}/disable`, { current_password: disPw, code: disCode.trim() });
      toast.success("Two-factor authentication turned off");
      setStep("idle"); setDisPw(""); setDisCode(""); onChange && onChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not turn off MFA"); }
    finally { setBusy(false); }
  };
  const copyAll = () => { navigator.clipboard?.writeText(recovery.join("\n")); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const downloadAll = () => {
    const blob = new Blob([`Lexarra recovery codes\n\n${recovery.join("\n")}\n\nEach code works once. Keep them somewhere safe.`], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "lexarra-recovery-codes.txt"; a.click();
  };

  // Recovery codes screen (shown once)
  if (step === "recovery") {
    return (
      <div data-testid="mfa-recovery-panel" className="space-y-4">
        <div className={`flex items-center gap-2 text-[14px] font-semibold ${dark ? "text-cyan-300" : "text-emerald-700"}`}><ShieldCheck size={18} /> Save your recovery codes</div>
        <p className={`text-[13px] leading-relaxed ${muted}`}>Store these somewhere safe. Each code can be used once if you ever lose access to your authenticator app. They won't be shown again.</p>
        <div className={`grid grid-cols-2 gap-2 rounded-xl p-4 font-mono text-[13px] ${dark ? "bg-white/[0.04] border border-white/10 text-white/90" : "bg-neutral-50 border border-neutral-200"}`} data-testid="mfa-recovery-codes">
          {recovery.map((c) => <div key={c} className="select-all">{c}</div>)}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={copyAll} data-testid="mfa-copy-recovery" className={`px-4 h-10 rounded-lg text-[13px] flex items-center gap-1.5 ${dark ? "border border-white/15 text-white/80" : "border border-neutral-300"}`}>{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}</button>
          <button onClick={downloadAll} data-testid="mfa-download-recovery" className={`px-4 h-10 rounded-lg text-[13px] flex items-center gap-1.5 ${dark ? "border border-white/15 text-white/80" : "border border-neutral-300"}`}><Download size={14} /> Download</button>
        </div>
        <label className={`flex items-start gap-2 text-[13px] ${muted}`}><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="mfa-recovery-ack" className="mt-0.5" /> I've saved my recovery codes somewhere safe.</label>
        <button disabled={!ack} onClick={() => { onChange && onChange(true); setStep("idle"); }} data-testid="mfa-recovery-done" className={`${btnPrimary} w-full text-white`} style={{ background: dark ? "linear-gradient(90deg,#22d3ee,#8b5cf6)" : "#3a5a40" }}>Done</button>
      </div>
    );
  }

  // Reset-via-email screen (lost authenticator + recovery codes)
  if (step === "reset") {
    return (
      <div data-testid="mfa-reset-panel" className="space-y-3">
        <div className={`flex items-center gap-2 text-[14px] font-semibold ${dark ? "text-white" : "text-neutral-900"}`}><KeyRound size={18} /> Turn off two-factor by email</div>
        <p className={`text-[13px] leading-relaxed ${muted}`}>Enter the 6-digit code we just emailed you. This turns off two-factor authentication so you can set it up again.</p>
        <input value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="123456" data-testid="mfa-reset-code" className={`${input} text-center`} />
        <div className="flex gap-2">
          <button onClick={() => setStep("idle")} className={`px-4 h-11 rounded-lg text-[13.5px] ${dark ? "border border-white/15 text-white/70" : "border border-neutral-300"}`}>Cancel</button>
          <button disabled={busy || resetCode.length !== 6} onClick={confirmReset} data-testid="mfa-reset-confirm" className={`${btnPrimary} flex-1 bg-red-600 text-white`}>{busy ? "Turning off…" : "Turn off with code"}</button>
        </div>
      </div>
    );
  }

  // Scan / confirm screen
  if (step === "scan") {
    return (
      <div data-testid="mfa-scan-panel" className="space-y-4">
        <div className={`flex items-center gap-2 text-[14px] font-semibold ${dark ? "text-white" : "text-neutral-900"}`}><Smartphone size={18} /> Scan with your authenticator app</div>
        <p className={`text-[13px] leading-relaxed ${muted}`}>Use Google Authenticator, Authy, Microsoft Authenticator or 1Password. Scan the QR code, then enter the 6-digit code it shows.</p>
        {qr && <img src={qr} alt="MFA QR code" data-testid="mfa-qr" className="w-44 h-44 rounded-xl bg-white p-2 mx-auto" />}
        <div className={`text-center text-[12px] ${muted}`}>Can't scan? Enter this key manually:<div className="font-mono text-[12px] mt-1 select-all break-all" data-testid="mfa-secret">{secret}</div></div>
        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="123456" data-testid="mfa-confirm-code" className={`${input} text-center`} />
        <div className="flex gap-2">
          {!mandatory && <button onClick={() => setStep("idle")} className={`px-4 h-11 rounded-lg text-[13.5px] ${dark ? "border border-white/15 text-white/70" : "border border-neutral-300"}`}>Cancel</button>}
          <button disabled={busy || code.length !== 6} onClick={confirm} data-testid="mfa-confirm-btn" className={`${btnPrimary} flex-1 text-white`} style={{ background: dark ? "linear-gradient(90deg,#22d3ee,#8b5cf6)" : "#3a5a40" }}>{busy ? "Verifying…" : "Verify & turn on"}</button>
        </div>
      </div>
    );
  }

  // Disable screen
  if (step === "disable") {
    return (
      <div data-testid="mfa-disable-panel" className="space-y-3">
        <p className={`text-[13px] ${muted}`}>Confirm your password and a current authenticator (or recovery) code to turn off two-factor authentication.</p>
        <input type="password" value={disPw} onChange={(e) => setDisPw(e.target.value)} placeholder="Current password" data-testid="mfa-disable-pw" className={input.replace("tracking-widest", "")} />
        <input value={disCode} onChange={(e) => setDisCode(e.target.value)} placeholder="Authenticator or recovery code" data-testid="mfa-disable-code" className={input.replace("tracking-widest", "")} />
        <div className="flex gap-2">
          <button onClick={() => setStep("idle")} className={`px-4 h-11 rounded-lg text-[13.5px] ${dark ? "border border-white/15 text-white/70" : "border border-neutral-300"}`}>Cancel</button>
          <button disabled={busy || !disPw || !disCode} onClick={disable} data-testid="mfa-disable-confirm" className={`${btnPrimary} flex-1 bg-red-600 text-white`}>{busy ? "Turning off…" : "Turn off MFA"}</button>
        </div>
      </div>
    );
  }

  // Idle
  if (enabled && !setupOnly) {
    return (
      <div data-testid="mfa-enabled-panel" className="flex items-center justify-between gap-3 flex-wrap">
        <div className={`flex items-center gap-2 text-[13.5px] ${dark ? "text-cyan-300" : "text-emerald-700"} font-medium`}><ShieldCheck size={17} /> Two-factor authentication is on{mandatory && <span className={`text-[12px] ${muted}`}>(required)</span>}</div>
        <div className="flex items-center gap-3">
          {emailReset && <button onClick={requestReset} disabled={busy} data-testid="mfa-email-reset" className={`text-[13px] ${muted} hover:underline`}>Lost your device? Reset by email</button>}
          {!mandatory && <button onClick={() => setStep("disable")} data-testid="mfa-turn-off" className={`text-[13px] ${muted} hover:underline`}>Turn off</button>}
        </div>
      </div>
    );
  }
  return (
    <div data-testid="mfa-idle-panel" className="space-y-3">
      <div className={`flex items-center gap-2 text-[13.5px] ${muted}`}><KeyRound size={17} /> Add an extra layer of security with an authenticator app.{mandatory && <span className={dark ? "text-amber-300" : "text-amber-600"}> Required for your account.</span>}</div>
      <button disabled={busy} onClick={start} data-testid="mfa-setup-start" className={`${btnPrimary} text-white`} style={{ background: dark ? "linear-gradient(90deg,#22d3ee,#8b5cf6)" : "#3a5a40" }}>{busy ? "Starting…" : "Set up two-factor authentication"}</button>
    </div>
  );
}
